import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import moment from 'moment';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { useAssociatedReviewLogs } from '@hooks-clean/use-associated-review-logs';
import { Action, IntegrationState } from '@interfaces-clean/actions.interface';
import { ObservationType, Owner } from '@interfaces-clean/observations.interface';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { NamedItemType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { GET_INTEGRATION_FIELDS_AND_CREATED } from '@queries/central-monitoring';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getIntegrationFieldsAndCreated_OpraQueries } from '@queries/generated/central-monitoring.generated';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
    getNamedItemsByTypeVariables,
} from '@queries/generated/common.generated';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface EditActionForm {
    site: string | null;
    status: string;
    type: ObservationType;
    requestedDate: Date | null;
    dueDate: Date | null;
    owner: Owner | null;
    responsibleFunction: { ref: string; name: string; groupedBy: string } | null;
    description: string | null;
    comments: string | null;
    resolution: string | null;
    completedDate: Date | null;
    created_date: Date;
    updated_date: Date | null;
    created_by: string;
    updated_by: string | null;
    associatedReviewLog: { id?: number | null; datasetDate?: string | null } | null;
    integrationEnabled: boolean | null;
    configFileUploadTime: string | null;
}

interface Props {
    action: Action;
    studyRef: string | null;
    onSubmit: (data: EditActionForm) => void;
    onClose: () => void;
    type: ObservationType;
}

const COMPONENT_NAME = 'edit-action-dialog';

const EditActionDialog = ({ action, studyRef, onSubmit, onClose, type }: Props) => {
    const { t } = useTranslation('cm');
    const { state } = useLocalState();
    const study = state?.selectedStudy;
    const [commentsExpanded, setCommentsExpanded] = useState<boolean>(false);
    const [resolutionExpanded, setResolutionExpanded] = useState<boolean>(false);

    const { data: integrationConfig, loading: loadingIntegrationFields } =
        useQuery<getIntegrationFieldsAndCreated_OpraQueries>(GET_INTEGRATION_FIELDS_AND_CREATED, {
            variables: {
                studyId: studyRef,
            },
            skip:
                type !== ObservationType.Site ||
                study?.integrationActive !== true ||
                action.integration_enabled !== IntegrationState.PushRequested,
        });

    const { data: observation, isLoading: loadingObservation } = ObservationsApi.useGetDetails(
        studyRef || '',
        action.observation_id,
        type,
    );

    const { data: associatedReviewLogs, isLoading: loadingReviewLogs } = useAssociatedReviewLogs(
        type,
        studyRef,
        action.observation_id,
    );

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditActionForm>({
        mode: 'all',
        defaultValues: {
            type: ObservationType.Site,
            requestedDate: action.requested_date ? new Date(action.requested_date) : null,
            dueDate: action.due_date ? new Date(action.due_date) : null,
            owner: null,
            responsibleFunction: action.responsibleFunction ? { name: action.responsibleFunction, ref: '' } : null,
            description: action.description,
            comments: action.comments ?? '',
            resolution: action.resolution ?? '',
            completedDate: action.completed_date ? new Date(action.completed_date) : null,
            // This will be set in a useEffect after the observation review log options are fetched
            associatedReviewLog: null,
            integrationEnabled: action.integration_enabled_bool === 'Yes',
        },
    });

    // Setting "configFileUploadTime" as we need it when submitting the form so that we send it to backend

    useEffect(() => {
        const configFileUploadTime = integrationConfig?.integrationFieldsAndCreated?.createdDate ?? null;
        setValue('configFileUploadTime', configFileUploadTime);
    }, [integrationConfig]);

    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string; disabled: boolean; active: boolean } | null)[] | undefined
    >([]);

    const isFieldLocked = (value: string): boolean => {
        return (
            (type === ObservationType.Site &&
                study?.integrationActive &&
                action?.integration_enabled === IntegrationState.PushRequested &&
                integrationConfig?.integrationFieldsAndCreated?.fields
                    ?.map((item) => item?.toLowerCase())
                    .includes(value.toLowerCase())) ??
            false
        );
    };

    useEffect(() => {
        if (action.associated_review_log && associatedReviewLogs) {
            const currentValue = associatedReviewLogs.find(
                (r) => r.datasetDate && r.datasetDate === action.associated_review_log,
            );

            // Use reset to set this as the default state (for correct isDirty logic)
            if (currentValue) {
                reset((currentValues) => {
                    return { ...currentValues, associatedReviewLog: currentValue };
                });
            }
        }
    }, [associatedReviewLogs]);

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter((user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember => !!user)
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    const aName = `${a?.firstName?.toLowerCase() ?? ''} ${a?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${b?.firstName?.toLowerCase() ?? ''} ${b?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((user) => ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    ref: user.id as string,
                    groupedBy: user.active ? 'Active' : 'Inactive',
                    disabled: action.owner
                        ? action.owner.toLowerCase() ===
                          `${user.firstName?.toLowerCase()} ${user.lastName?.toLowerCase()}`
                            ? false
                            : !user.active
                        : !user.active,
                    active: user.active ?? false,
                }));
            if (action.owner) {
                const currentOwner = studyUsers?.find((user) => user.fullName === action.owner) ?? null;
                // Use reset to set this as the default state (for correct isDirty logic)
                if (currentOwner) {
                    reset((currentValues) => {
                        return { ...currentValues, owner: currentOwner };
                    });
                }
            }
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    const [responsibleFunctions, setResponsibleFunctions] = useState<
        ({ groupedBy: string; ref: string; name: string; disabled: boolean; active: boolean } | null)[]
    >([]);

    useEffect(() => {
        if (responsibleFunctionsData?.namedItemsByType) {
            const studyFunctions = responsibleFunctionsData?.namedItemsByType
                .filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return a?.active ? -1 : 1;
                })
                .map((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    ref: (studyFunction.id as string) ?? '',
                    disabled: action.responsible_function
                        ? action.responsible_function.toLowerCase() === studyFunction?.name?.toLowerCase()
                            ? false
                            : !studyFunction.active
                        : !studyFunction.active,
                    active: studyFunction.active ?? false,
                }));

            if (action.responsible_function) {
                const currentFunction =
                    studyFunctions?.find((studyFunction) => studyFunction.name === action.responsible_function) ?? null;
                if (currentFunction) {
                    reset((currentValues) => {
                        return { ...currentValues, responsibleFunction: currentFunction };
                    });
                }
            }
            setResponsibleFunctions(studyFunctions);
        }
    }, [responsibleFunctionsData?.namedItemsByType]);

    let dialogTitle = `${t('common:edit')}`;
    switch (type) {
        case ObservationType.Site:
            dialogTitle += ` ${t('actionDialog.siteTitle')}: ${action.record_id}`;
            break;
        case ObservationType.Study:
            dialogTitle += ` ${t('actionDialog.studyTitle')}: ${action.record_id}`;
            break;
        case ObservationType.Controls:
            dialogTitle += ` ${t('actionDialog.controlsTitle')}: ${action.record_id}`;
            break;
        default:
            dialogTitle = '';
    }

    const dialogHeight = 688;
    const dialogWidth = 612;

    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(action?.created_date).format('DD-MMM-YYYY')} by ${action?.created_by}`;
    const updatedBy = `${moment(action?.updated_date).format('DD-MMM-YYYY')} by ${action?.updated_by}`;
    const integrationUpdatedBy =
        type === ObservationType.Site && action?.integration_updated !== null
            ? stringDateWithoutTimeFromUtcNonNullable(action.integration_updated, 'DD-MMM-YYYY [at] HH:mm')
            : undefined;

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            createdUpdatedInfo={
                <CreatedUpdatedInfo
                    created={createdBy}
                    updated={updatedBy}
                    integrationUpdated={integrationUpdatedBy}
                    width={type === ObservationType.Site ? 500 : 600}
                />
            }
            createdUpdatedBackgroundColour="gray"
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={dialogHeight}
            initialWidth={dialogWidth}
            additionalInfo={
                <ObservationDetails
                    observation={observation || undefined}
                    observationLoading={loadingObservation}
                    type={type}
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="fit-content"
        >
            <div
                className={cn(
                    style.editSiteObservationDialog,
                    style.rightBorderStyle,
                    'k-w-full',
                    'k-h-full',
                    'k-d-flex-col',
                )}
            >
                {loadingObservation || loadingIntegrationFields ? (
                    <Loading />
                ) : (
                    <div>
                        <div className={cn(style.editActionInfo)}>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left">
                                <div className="k-d-flex !k-basis-1/2 k-gap-sm">
                                    <div className="k-font-medium">{t('actionDialog.status')}</div>
                                    <div className={cn(style.editSiteObservationTopInfoLabel)}>{action.status}</div>
                                </div>
                                <div className="k-d-flex !k-basis-1/2 k-gap-sm">
                                    {type === ObservationType.Site && (
                                        <>
                                            <div className={cn('k-font-medium', style.topInfoDivider)}>
                                                {t('actionDialog.site')}
                                            </div>
                                            <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                                {action.site}
                                            </div>
                                        </>
                                    )}

                                    {type === ObservationType.Controls && (
                                        <>
                                            <div className={cn('k-font-medium', style.topInfoDivider)}>
                                                {t('actionDialog.timePeriod')}
                                            </div>
                                            <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                                {observation?.timePeriod}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {type === ObservationType.Site &&
                                action.integration_enabled === IntegrationState.PushRequested && (
                                    <div className="k-shrink-0 k-d-flex k-justify-content-left">
                                        <div className="k-d-flex k-gap-sm !k-basis-1/2">
                                            <div className="k-font-medium">{t('actionDialog.integrationEnabled')}</div>
                                            <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                                {action.integration_enabled_bool}
                                            </div>
                                        </div>
                                        <div className="k-d-flex !k-basis-1/2 k-gap-sm">
                                            <div className={cn('k-font-medium', style.topInfoDivider)}>
                                                {t('actionDialog.sourceSystemId')}
                                            </div>
                                            <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                                {action.integration_system_id}
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>
                        <form id="add-action" className="k-mt-sm k-w-full k-pl-3 k-pr-3">
                            <div
                                className={cn(
                                    'k-shrink-0 k-d-flex k-justify-content-center k-pb-md',
                                    style.formFieldGap,
                                )}
                            >
                                <div className="!k-basis-1/2">
                                    <BasicDropDownController
                                        label={t('actionDialog.associatedReviewLog')}
                                        control={control}
                                        name="associatedReviewLog"
                                        data-testid="add-action-associated-review-log"
                                        data={associatedReviewLogs ?? []}
                                        textField="datasetDate"
                                        dataItemKey="id"
                                        filterable
                                        loading={loadingReviewLogs}
                                        allowClear
                                        readOnly={isSubmitting || isFieldLocked('AssociatedReviewLog')}
                                    />
                                </div>
                                <div className="!k-basis-1/2 k-pt-5">
                                    {type === ObservationType.Site &&
                                        study?.integrationActive &&
                                        action.integration_enabled !== IntegrationState.PushRequested && (
                                            <CheckboxController
                                                data-testid="integration-enabled"
                                                label={t('actionDialog.integrationEnabled')}
                                                name="integrationEnabled"
                                                control={control}
                                            />
                                        )}
                                </div>
                            </div>

                            <div
                                className={cn(
                                    'k-shrink-0 k-d-flex k-justify-content-center k-pb-md',
                                    style.formFieldGap,
                                )}
                            >
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="requestedDate"
                                        data-testid="add-action-requested-date"
                                        fillMode="solid"
                                        required
                                        label={t('actionDialog.requestedDate')}
                                        readonly={isSubmitting || isFieldLocked('RequestedDate')}
                                    />
                                </div>
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="dueDate"
                                        data-testid="add-action-due-date"
                                        fillMode="solid"
                                        label={t('actionDialog.dueDate')}
                                        readonly={isSubmitting || isFieldLocked('DueDate')}
                                    />
                                </div>
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                                <BasicDropDownController
                                    label={t('actionDialog.owner')}
                                    control={control}
                                    name="owner"
                                    data-testid="add-action-owner"
                                    data={studyMembers ?? []}
                                    textField="fullName"
                                    groupField="groupedBy"
                                    dataItemKey="ref"
                                    filterable
                                    loading={loadingStudyMembers}
                                    allowClear
                                    activeField="active"
                                    readOnly={isSubmitting || isFieldLocked('Owner')}
                                />
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                                <BasicDropDownController
                                    label={t('actionDialog.responsibleFunction')}
                                    control={control}
                                    name="responsibleFunction"
                                    data-testid="add-action-responsible-function"
                                    data={responsibleFunctions ?? []}
                                    textField="name"
                                    groupField="groupedBy"
                                    dataItemKey="ref"
                                    filterable
                                    loading={loadingResponsibleFunctions}
                                    allowClear
                                    activeField="active"
                                    readOnly={isSubmitting || isFieldLocked('ResponsibleFunction')}
                                />
                            </div>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left k-gap-1 k-pb-md">
                                <div className="!k-basis-1/2">
                                    <DatePickerController
                                        control={control}
                                        name="completedDate"
                                        data-testid="add-action-completed-date"
                                        fillMode="solid"
                                        label={t('actionDialog.completedDate')}
                                        className="k-w-full"
                                        readonly={isSubmitting || isFieldLocked('CompletedDate')}
                                    />
                                </div>
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="description"
                                    required
                                    data-testid="add-action-description"
                                    label={t('actionDialog.description')}
                                    rows={4}
                                    readOnly={isSubmitting || isFieldLocked('Description')}
                                />
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaDropdownController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="comments"
                                    data-testid="field-select-comments"
                                    label={t('actionDialog.comments')}
                                    rows={4}
                                    handleInputChange={async (event) => {
                                        event.value.length > 0 ? setCommentsExpanded(true) : setCommentsExpanded(false);
                                    }}
                                    expanded={commentsExpanded || !!action.comments}
                                    readOnly={isSubmitting || isFieldLocked('Comments')}
                                />
                            </div>
                            <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                                <TextAreaDropdownController
                                    maxLength={3000}
                                    control={control}
                                    className={style.textAreaOverrides}
                                    name="resolution"
                                    data-testid="edit-action-resolution"
                                    label={t('actionDialog.resolution')}
                                    rows={4}
                                    handleInputChange={async (event) => {
                                        event.value.length > 0
                                            ? setResolutionExpanded(true)
                                            : setResolutionExpanded(false);
                                    }}
                                    expanded={resolutionExpanded || !!action.resolution}
                                    readOnly={isSubmitting || isFieldLocked('Resolution')}
                                />
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </DraggableDialog>
    );
};

export default EditActionDialog;
